import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add,
  Security,
  Edit,
  Delete,
  ExpandMore,
  Warning,
  CheckCircle,
  Cancel,
  Info,
  Shield,
  BugReport,
  Block,
  FileUpload,
  History,
  Gavel,
  Flag,
  NotificationImportant,
  Description,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { SecurityRule } from '../types/api';
import DocumentUpload from '../components/DocumentUpload';
import DocumentProcessingHistory from '../components/DocumentProcessingHistory';

// Remove local interface - use the one from api.ts instead

interface ComplianceFramework {
  name: string;
  display_name: string;
  version: string;
  regulatory_authority: string;
  jurisdiction: string;
  security_rules: Array<{
    id: string;
    name: string;
    description: string;
    pattern: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    action: 'block' | 'flag' | 'alert' | 'log';
    confidence_threshold: number;
  }>;
}

const SecurityRules: React.FC = () => {
  const [rules, setRules] = useState<SecurityRule[]>([]);
  const [complianceFrameworks, setComplianceFrameworks] = useState<ComplianceFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [complianceLoading, setComplianceLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [documentUploadOpen, setDocumentUploadOpen] = useState(false);
  const [processingHistoryOpen, setProcessingHistoryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFramework, setExpandedFramework] = useState<string | false>(false);
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    direction: 'inbound' as SecurityRule['direction'],
    rule_type: 'keyword' as SecurityRule['rule_type'],
    pattern: '',
    action: 'flag' as SecurityRule['action'],
    severity: 'medium' as SecurityRule['severity'],
    category: 'custom' as SecurityRule['category'],
    confidence_threshold: 0.5,
    response_action: {
      replacement_text: '',
      notify_admin: false,
      log_level: 'info' as 'info' | 'warn' | 'error'
    },
    tags: '',
  });

  useEffect(() => {
    fetchRules();
    fetchComplianceFrameworks();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try admin endpoint first, fall back to basic info if not admin
      const endpoint = user?.role === 'admin' ? '/api/admin/security-rules' : '/api/security-rules';
      const response = await axios.get(endpoint);
      
      setRules(response.data.rules || []);
    } catch (error: any) {
      console.error('Failed to fetch security rules:', error);
      
      if (error.response?.status === 403) {
        setError('You need admin privileges to manage security rules');
      } else if (error.response?.status === 404) {
        // Endpoint doesn't exist, show default rules info
        setError(null);
        setRules([]);
      } else {
        setError('Failed to load security rules');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchComplianceFrameworks = async () => {
    try {
      setComplianceLoading(true);
      const response = await axios.get('/api/compliance/frameworks');
      setComplianceFrameworks(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch compliance frameworks:', error);
      // Don't show error, just leave empty
      setComplianceFrameworks([]);
    } finally {
      setComplianceLoading(false);
    }
  };

  const handleCreateRule = async () => {
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
      };

      const response = await axios.post('/api/admin/security-rules', payload);
      setCreateDialogOpen(false);
      fetchRules();
      toast.success('Security rule created successfully!');
      resetForm();
    } catch (error: any) {
      console.error('Failed to create security rule:', error);
      toast.error(error.response?.data?.error || 'Failed to create security rule');
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this security rule?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/security-rules/${ruleId}`);
      fetchRules();
      toast.success('Security rule deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete security rule:', error);
      toast.error(error.response?.data?.error || 'Failed to delete security rule');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      direction: 'inbound',
      rule_type: 'keyword',
      pattern: '',
      action: 'flag',
      severity: 'medium',
      category: 'custom',
      confidence_threshold: 0.5,
      response_action: {
        replacement_text: '',
        notify_admin: false,
        log_level: 'info'
      },
      tags: '',
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'block': return <Block />;
      case 'flag': return <Warning />;
      case 'redact': return <Edit />;
      case 'modify': return <Edit />;
      case 'alert': return <Info />;
      case 'log': return <Description />;
      default: return <Security />;
    }
  };

  const getComplianceActionIcon = (action: string) => {
    switch (action) {
      case 'block': return <Block fontSize="small" />;
      case 'flag': return <Flag fontSize="small" />;
      case 'alert': return <NotificationImportant fontSize="small" />;
      case 'log': return <Description fontSize="small" />;
      default: return <Security fontSize="small" />;
    }
  };

  const getComplianceActionColor = (action: string) => {
    switch (action) {
      case 'block': return 'error';
      case 'flag': return 'warning';
      case 'alert': return 'info';
      case 'log': return 'default';
      default: return 'default';
    }
  };

  const handleAccordionChange = (frameworkName: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedFramework(isExpanded ? frameworkName : false);
  };

  const totalComplianceRules = Array.isArray(complianceFrameworks) 
    ? complianceFrameworks.reduce((sum, fw) => sum + (fw.security_rules?.length || 0), 0)
    : 0;

  const defaultRulesInfo = [
    {
      name: 'Prompt Injection Detection',
      description: 'Detects attempts to manipulate AI behavior through prompt injection techniques',
      category: 'injection',
      severity: 'high',
      status: 'Built-in',
    },
    {
      name: 'PII Detection',
      description: 'Identifies and protects personally identifiable information in prompts',
      category: 'pii',
      severity: 'high',
      status: 'Built-in',
    },
    {
      name: 'Jailbreak Prevention',
      description: 'Prevents attempts to bypass AI safety measures',
      category: 'jailbreak',
      severity: 'critical',
      status: 'Built-in',
    },
    {
      name: 'Hate Speech Detection',
      description: 'Detects discriminatory language, racial slurs, and hate-based content',
      category: 'hate_speech',
      severity: 'high',
      status: 'Built-in',
    },
    {
      name: 'Sexual Content Filter',
      description: 'Blocks explicit sexual content and inappropriate adult material',
      category: 'sexual_content',
      severity: 'high',
      status: 'Built-in',
    },
    {
      name: 'Violence Detection',
      description: 'Identifies violent threats, self-harm content, and harmful instructions',
      category: 'violence',
      severity: 'critical',
      status: 'Built-in',
    },
    {
      name: 'Legacy Toxicity Filter',
      description: 'General toxic content detection (includes combined moderation rules)',
      category: 'toxic',
      severity: 'medium',
      status: 'Built-in',
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Security Rules
        </Typography>
        {user?.role === 'admin' && (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<History />}
              onClick={() => setProcessingHistoryOpen(true)}
            >
              Processing History
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUpload />}
              onClick={() => setDocumentUploadOpen(true)}
            >
              Upload Document
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Add Custom Rule
            </Button>
          </Box>
        )}
      </Box>

      <Typography variant="body1" color="textSecondary" paragraph>
        Security rules define how GuardLLM detects and handles potential threats in prompts and responses.
      </Typography>

      {error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Built-in Rules Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Shield color="primary" />
            <Typography variant="h6">Built-in Security Rules</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" paragraph>
            These core security rules are always active and protect against common threats.
          </Typography>
          
          <Grid container spacing={2}>
            {defaultRulesInfo.map((rule, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {rule.name}
                    </Typography>
                    <Chip
                      label={rule.status}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {rule.description}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={rule.severity.toUpperCase()}
                      size="small"
                      color={getSeverityColor(rule.severity) as any}
                    />
                    <Chip
                      label={rule.category.toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Compliance Framework Security Rules Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Gavel color="secondary" />
              <Typography variant="h6">Compliance Framework Security Rules</Typography>
            </Box>
            <Chip 
              label={`${totalComplianceRules} Total Rules`} 
              color="secondary" 
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="textSecondary" paragraph>
            Security rules defined by compliance frameworks (HIPAA, GDPR, SOC 2, ISO 27001, NIST 800-53).
          </Typography>

          {complianceLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : !Array.isArray(complianceFrameworks) || complianceFrameworks.length === 0 ? (
            <Alert severity="info">
              No compliance frameworks are currently enabled. Enable frameworks in Organization Settings to see their security rules.
            </Alert>
          ) : (
            complianceFrameworks.map((framework) => (
              <Accordion
                key={framework.name}
                expanded={expandedFramework === framework.name}
                onChange={handleAccordionChange(framework.name)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {framework.display_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {framework.regulatory_authority} • {framework.jurisdiction} • v{framework.version}
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${framework.security_rules?.length || 0} Rules`}
                      size="small"
                      color="secondary"
                      sx={{ mr: 2 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {framework.security_rules && framework.security_rules.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Rule Name</strong></TableCell>
                            <TableCell><strong>Description</strong></TableCell>
                            <TableCell align="center"><strong>Severity</strong></TableCell>
                            <TableCell align="center"><strong>Action</strong></TableCell>
                            <TableCell align="center"><strong>Threshold</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {framework.security_rules.map((rule) => (
                            <TableRow key={rule.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {rule.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {rule.id}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {rule.description}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={rule.severity.toUpperCase()}
                                  size="small"
                                  color={getSeverityColor(rule.severity) as any}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  icon={getComplianceActionIcon(rule.action)}
                                  label={rule.action.toUpperCase()}
                                  size="small"
                                  color={getComplianceActionColor(rule.action) as any}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {(rule.confidence_threshold * 100).toFixed(0)}%
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">
                      No security rules defined for this framework.
                    </Alert>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </CardContent>
      </Card>

      {/* Custom Rules Section */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <BugReport color="secondary" />
            <Typography variant="h6">Custom Security Rules</Typography>
          </Box>
          
          {user?.role !== 'admin' ? (
            <Alert severity="info">
              Custom security rules can only be managed by administrators. 
              Contact your admin to create custom rules for your specific use case.
            </Alert>
          ) : rules.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Security sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Custom Rules Configured
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Create custom security rules to detect specific patterns or threats relevant to your use case.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create First Rule
              </Button>
            </Paper>
          ) : (
            <List>
              {rules.map((rule) => (
                <ListItem key={rule.id} divider>
                  <ListItemIcon>
                    {getActionIcon(rule.action)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">{rule.name}</Typography>
                        <Chip
                          label={rule.severity.toUpperCase()}
                          size="small"
                          color={getSeverityColor(rule.severity) as any}
                        />
                        {rule.is_active ? (
                          <Chip label="ACTIVE" size="small" color="success" />
                        ) : (
                          <Chip label="INACTIVE" size="small" color="default" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {rule.description || 'No description'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Direction: {rule.direction || 'inbound'} • Type: {rule.rule_type} • Action: {rule.action} • Matches: {rule.match_count}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Delete Rule">
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteRule(rule.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Create Rule Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Custom Security Rule</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rule Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={formData.direction}
                  onChange={(e) => setFormData({ ...formData, direction: e.target.value as SecurityRule['direction'] })}
                >
                  <MenuItem value="inbound">Inbound (Filter Prompts)</MenuItem>
                  <MenuItem value="outbound">Outbound (Filter Responses)</MenuItem>
                  <MenuItem value="both">Both Directions</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Rule Type</InputLabel>
                <Select
                  value={formData.rule_type}
                  onChange={(e) => setFormData({ ...formData, rule_type: e.target.value as SecurityRule['rule_type'] })}
                >
                  <MenuItem value="keyword">Keyword</MenuItem>
                  <MenuItem value="regex">Regular Expression</MenuItem>
                  <MenuItem value="pattern_match">Pattern Match</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={formData.action}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value as SecurityRule['action'] })}
                >
                  <MenuItem value="flag">Flag</MenuItem>
                  <MenuItem value="block">Block</MenuItem>
                  <MenuItem value="redact">Redact</MenuItem>
                  <MenuItem value="modify">Modify</MenuItem>
                  <MenuItem value="alert">Alert</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={formData.severity}
                  onChange={(e) => setFormData({ ...formData, severity: e.target.value as SecurityRule['severity'] })}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as SecurityRule['category'] })}
                >
                  <MenuItem value="custom">Custom</MenuItem>
                  <MenuItem value="injection">Injection</MenuItem>
                  <MenuItem value="pii">PII</MenuItem>
                  <MenuItem value="jailbreak">Jailbreak</MenuItem>
                  <MenuItem value="hate_speech">Hate Speech</MenuItem>
                  <MenuItem value="sexual_content">Sexual Content</MenuItem>
                  <MenuItem value="violence">Violence</MenuItem>
                  <MenuItem value="toxic">Toxic (Legacy)</MenuItem>
                  <MenuItem value="data_extraction">Data Extraction</MenuItem>
                  <MenuItem value="data_leakage">Data Leakage</MenuItem>
                  <MenuItem value="proprietary_info">Proprietary Info</MenuItem>
                  <MenuItem value="credentials">Credentials</MenuItem>
                  <MenuItem value="api_exposure">API Exposure</MenuItem>
                  <MenuItem value="code_exposure">Code Exposure</MenuItem>
                  <MenuItem value="system_info">System Info</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pattern"
                value={formData.pattern}
                onChange={(e) => setFormData({ ...formData, pattern: e.target.value })}
                multiline
                rows={3}
                helperText="Enter the pattern to match (keywords, regex, etc.)"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                helperText="Optional tags for organizing rules"
              />
            </Grid>

            {/* Response Action Configuration for Outbound Rules */}
            {(formData.direction === 'outbound' || formData.direction === 'both') && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" color="primary">
                    Response Action Configuration
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Configure how responses should be handled when this rule is triggered
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Replacement Text"
                    value={formData.response_action?.replacement_text || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      response_action: { 
                        ...formData.response_action, 
                        replacement_text: e.target.value 
                      } 
                    })}
                    helperText="Text to use when redacting content (optional)"
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Log Level</InputLabel>
                    <Select
                      value={formData.response_action?.log_level || 'info'}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        response_action: { 
                          ...formData.response_action, 
                          log_level: e.target.value as 'info' | 'warn' | 'error' 
                        } 
                      })}
                    >
                      <MenuItem value="info">Info</MenuItem>
                      <MenuItem value="warn">Warning</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <input
                      type="checkbox"
                      id="notify-admin"
                      checked={formData.response_action?.notify_admin || false}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        response_action: { 
                          ...formData.response_action, 
                          notify_admin: e.target.checked 
                        } 
                      })}
                    />
                    <label htmlFor="notify-admin" style={{ marginLeft: 8 }}>
                      Notify administrators when this rule is triggered
                    </label>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateRule} variant="contained" disabled={!formData.name || !formData.pattern}>
            Create Rule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Upload Dialog */}
      <DocumentUpload
        open={documentUploadOpen}
        onClose={() => setDocumentUploadOpen(false)}
        onSuccess={() => {
          setDocumentUploadOpen(false);
          fetchRules(); // Refresh rules after successful upload
        }}
      />

      {/* Document Processing History Dialog */}
      <DocumentProcessingHistory
        open={processingHistoryOpen}
        onClose={() => setProcessingHistoryOpen(false)}
      />
    </Box>
  );
};

export default SecurityRules;